import { takeEvery, all, put, call, takeLatest } from 'redux-saga/effects'
import {
  connectionsRequest,
  addConnections,
  CONNECTION_DELETE_REQUEST,
  setDeleteConnectionStatus,
  setDeleteConnectionError,
  setCreateConnectionStatus,
  setCreateConnectionError,
  setMyConnectionsStatus,
  setMyConnectionsError,
  BQ_CONNECTION_PROJECTS_FETCH_REQUEST,
  setBigQueryConnectionProjects,
  setBigQueryConnectionProjectsStatus,
  setBigQueryConnectionProjectsError,
  BQ_CONNECTION_DATASETS_FETCH_REQUEST,
  setBigQueryConnectionDatasets,
  setBigQueryConnectionDatasetsStatus,
  setBigQueryConnectionDatasetsError,
  setBigqueryConnectionCreatorProjects,
  setBigqueryConnectionCreatorProjectsError,
  setBigqueryConnectionCreatorProjectsStatus,
  setConnectionRelatedMaps,
  setConnectionRelatedMapsStatus,
  setCurrentConnection,
  setCurrentConnectionStatus,
  setCurrentConnectionError,
  CONNECTION_FETCH_ONE_REQUEST,
  createConnectionRequest,
  fetchConnectionMapsRelated,
  trackConnectionProviderSelected,
  fetchRecentResourcesRequest,
  setRecentResourcesStatus,
  setRecentResourcesError,
  setOAuthBillingProjectsError
} from './connectionsSlice'
import {
  saveConnection as saveConnectionRequest,
  deleteConnection,
  getBigQueryConnectionProjects,
  getBigQueryConnectionDatasets,
  getConnectionRelatedMaps,
  getConnection,
  trackConnectionProviderSelectedRequest,
  getOAuthBillingProjectsRequest,
  getRecentResources
} from '../api/connections'
import { setStatsStatus } from 'features/account/state/accountSlice'
import { BigQueryConnectionDataset, BigQueryConnectionProject, Connection, Dataset } from '../types'
import { PayloadAction } from '@reduxjs/toolkit'
import { CartoApiError, PaginatedResponse, RequestStatus } from 'features/common'
import { Map } from 'features/maps/types'
import {
  oauthBillingProjectsRequest,
  setOAuthBillingProjects,
  setOAuthBillingProjectsStatus,
  fetchBQConnectionCreatorProjectsRequest,
  setRecentResources
} from './connectionsSlice'
import { setDeleteConnectionFromDataTree } from 'features/data-explorer/state/dataExplorerSlice'
import { User } from 'features/users'
import { setMyMapsStatus } from 'features/maps/state/mapsSlice'
import {
  addToUpdatingConnectionsToolbox,
  deleteFromUpdatingConnectionsToolbox,
  updateAnalyticsToolboxRequest,
  updateConnectionFromList
} from '..'
import { createApiError } from 'features/common/api/apiError'
import { queryClient, queryClientUpdateCacheArrayElement } from 'queryClient'
import { connectionsQuery, connectionsQueryKeys } from '../api/connectionsQueries'

function* fetchConnections() {
  try {
    yield put(setMyConnectionsStatus(RequestStatus.Pending))
    const connections: Connection[] = yield queryClient.fetchQuery(connectionsQuery)
    yield put(addConnections(connections))
    yield put(setMyConnectionsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setMyConnectionsStatus(RequestStatus.Rejected))
    yield put(setMyConnectionsError(createApiError(e)))
  }
}

function* fetchConnection(action: PayloadAction<string>) {
  try {
    yield put(setCurrentConnectionStatus(RequestStatus.Pending))
    const connection: Connection = yield call(getConnection, action.payload)
    if (connection.invalidOAuth) {
      connection.config.billing_project = undefined
    }

    yield put(setCurrentConnection(connection))
    yield put(setCurrentConnectionStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setCurrentConnectionStatus(RequestStatus.Rejected))
    yield put(setCurrentConnectionError(createApiError(e)))
  }
}

function* fetchRecentResources(action?: PayloadAction<number>) {
  try {
    yield put(setRecentResourcesStatus(RequestStatus.Pending))
    const resources: Array<Dataset> = yield call(getRecentResources, action?.payload)
    yield put(setRecentResources(resources))
    yield put(setRecentResourcesStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setRecentResourcesStatus(RequestStatus.Rejected))
    yield put(setRecentResourcesError(createApiError(e)))
  }
}

function* removeConnection(action: PayloadAction<string>) {
  try {
    yield put(setDeleteConnectionStatus(RequestStatus.Pending))
    yield call(deleteConnection, action.payload)
    yield put(setDeleteConnectionFromDataTree(action.payload))
    yield put(setDeleteConnectionStatus(RequestStatus.Fulfilled))
    yield put(setDeleteConnectionStatus(RequestStatus.Idle))
    yield put(setMyConnectionsStatus(RequestStatus.Idle))
    queryClient.invalidateQueries(connectionsQueryKeys.all)
    yield put(setStatsStatus(RequestStatus.Idle))
    yield put(setMyMapsStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setDeleteConnectionStatus(RequestStatus.Rejected))
    yield put(setDeleteConnectionError(e as CartoApiError))
  }
}

function* saveConnection(action: PayloadAction<Connection>) {
  const { id } = action.payload

  try {
    yield put(setCreateConnectionStatus(RequestStatus.Pending))
    yield put(addToUpdatingConnectionsToolbox(id))

    const savedConnection = yield call(saveConnectionRequest, action.payload)
    yield put(deleteFromUpdatingConnectionsToolbox(id))
    yield put(setCreateConnectionStatus(RequestStatus.Fulfilled))
    // Avoid fetching the connections list again
    if (id) {
      yield put(updateConnectionFromList(savedConnection))
      queryClientUpdateCacheArrayElement(connectionsQueryKeys.connections, savedConnection)
      queryClient.setQueryData([connectionsQueryKeys.connection, id], savedConnection)
    } else {
      yield put(setMyConnectionsStatus(RequestStatus.Idle))
      queryClient.invalidateQueries(connectionsQueryKeys.all)
      yield put(setStatsStatus(RequestStatus.Idle))
    }
  } catch (e) {
    yield put(setCreateConnectionStatus(RequestStatus.Rejected))
    yield put(setCreateConnectionError(e as CartoApiError))
    yield put(deleteFromUpdatingConnectionsToolbox(id))
  }
}

function* fetchBQConnectionProjects(action: PayloadAction<string>) {
  try {
    yield put(setBigQueryConnectionProjectsStatus(RequestStatus.Pending))
    const projects: Array<BigQueryConnectionProject> = yield call(getBigQueryConnectionProjects, action.payload)
    yield put(setBigQueryConnectionProjectsStatus(RequestStatus.Fulfilled))
    yield put(setBigQueryConnectionProjects(projects))
  } catch (e) {
    yield put(setBigQueryConnectionProjectsStatus(RequestStatus.Rejected))
    yield put(setBigQueryConnectionProjectsError(createApiError(e)))
  }
}

function* fetchBigqueryCreatorProjects(action: PayloadAction<string>) {
  try {
    yield put(setBigqueryConnectionCreatorProjectsStatus(RequestStatus.Pending))
    const projects: Array<BigQueryConnectionProject> = yield call(getBigQueryConnectionProjects, action.payload)
    yield updateBigqueryConnectionCreatorProjects(projects)
  } catch (e) {
    yield put(setBigqueryConnectionCreatorProjectsStatus(RequestStatus.Rejected))
    yield put(setBigqueryConnectionCreatorProjectsError(e as CartoApiError))
  }
}

function* updateBigqueryConnectionCreatorProjects(projects: Array<BigQueryConnectionProject>) {
  try {
    yield put(setBigqueryConnectionCreatorProjects(projects))
    yield put(setBigqueryConnectionCreatorProjectsStatus(RequestStatus.Fulfilled))
    yield put(setBigqueryConnectionCreatorProjectsStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setBigqueryConnectionCreatorProjectsStatus(RequestStatus.Rejected))
    yield put(setBigqueryConnectionCreatorProjectsError(e as CartoApiError))
  }
}

function* fetchBQConnectionDatasets(action: PayloadAction<{ connectionId: string; projectId: string }>) {
  try {
    yield put(setBigQueryConnectionDatasetsStatus(RequestStatus.Pending))
    const datasets: Array<BigQueryConnectionDataset> = yield call(getBigQueryConnectionDatasets, action.payload)
    yield put(setBigQueryConnectionDatasetsStatus(RequestStatus.Fulfilled))
    yield put(setBigQueryConnectionDatasets(datasets))
  } catch (e) {
    yield put(setBigQueryConnectionDatasetsStatus(RequestStatus.Rejected))
    yield put(setBigQueryConnectionDatasetsError(createApiError(e)))
  }
}

function* fetchConnectionRelatedMaps(action: PayloadAction<string>) {
  try {
    yield put(setConnectionRelatedMapsStatus(RequestStatus.Pending))
    const relatedMaps: PaginatedResponse<Map> = yield call(getConnectionRelatedMaps, action.payload)
    yield put(setConnectionRelatedMapsStatus(RequestStatus.Fulfilled))
    yield put(setConnectionRelatedMaps(relatedMaps))
  } catch (e) {
    yield put(setConnectionRelatedMapsStatus(RequestStatus.Rejected))
  }
}

function* trackConnectionProviderSelectedMetric(action: PayloadAction<{ user: User; providerId: string }>) {
  try {
    yield call(trackConnectionProviderSelectedRequest, action.payload)
  } catch (e) {
    // TODO
  }
}

function* getOAuthBillingProjects(action: PayloadAction<{ state: string; client_email: string }>) {
  try {
    yield put(setOAuthBillingProjectsStatus(RequestStatus.Pending))
    const projects: Array<BigQueryConnectionProject> = yield call(getOAuthBillingProjectsRequest, action.payload)
    yield put(setOAuthBillingProjects(projects))
    yield put(setOAuthBillingProjectsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setOAuthBillingProjectsStatus(RequestStatus.Rejected))
    yield put(setOAuthBillingProjectsError(e as CartoApiError))
  }
}

function* watchFetchConnections() {
  yield takeLatest(connectionsRequest.type, fetchConnections)
  yield takeEvery(fetchBQConnectionCreatorProjectsRequest.type, fetchBigqueryCreatorProjects)
  yield takeEvery(createConnectionRequest.type, saveConnection)
  yield takeEvery(fetchConnectionMapsRelated.type, fetchConnectionRelatedMaps)
  yield takeEvery(CONNECTION_FETCH_ONE_REQUEST, fetchConnection)
  yield takeEvery(CONNECTION_DELETE_REQUEST, removeConnection)
  yield takeEvery(BQ_CONNECTION_PROJECTS_FETCH_REQUEST, fetchBQConnectionProjects)
  yield takeEvery(BQ_CONNECTION_DATASETS_FETCH_REQUEST, fetchBQConnectionDatasets)
  yield takeEvery(trackConnectionProviderSelected.type, trackConnectionProviderSelectedMetric)
  yield takeEvery(oauthBillingProjectsRequest.type, getOAuthBillingProjects)
  yield takeEvery(updateAnalyticsToolboxRequest.type, saveConnection)
  yield takeEvery(fetchRecentResourcesRequest.type, fetchRecentResources)
}

export default function* connectionsSaga() {
  try {
    yield all([watchFetchConnections()])
  } catch (e) {
    console.error(e)
  }
}
